import {concat, isEqual} from 'lodash';

import {ActionWithPayload} from '../../../helpers/reduxHelpers';
import {buildUniqueOnlineTeammateIdentifier} from '../../../models/push/onlineTeammateHelpers';
import {OnlineTeammate} from '../../../models/push/onlineTeammateModel';
import {AppAction} from '../../appActions';
import {ThunkAction} from '../../appThunk';
import {getOnlineTeammates, getUniqueOnlineTeammateIdentifiersSet} from './onlineTeammatesSelectors';

/*
 * Action types.
 */

export enum OnlineTeammatesActionsEnum {
  SET_ONLINE_TEAMMATES = '@@data/onlineTeammates/SET_ONLINE_TEAMMATES',
}

/*
 * Types.
 */

export interface SetOnlineTeammatesAction extends ActionWithPayload<ReadonlyArray<OnlineTeammate>> {
  type: OnlineTeammatesActionsEnum.SET_ONLINE_TEAMMATES;
}

/*
 * Type guard.
 */

export function isOnlineTeammatesAction(action: AppAction): action is OnlineTeammatesAction {
  return action.type === OnlineTeammatesActionsEnum.SET_ONLINE_TEAMMATES;
}

/*
 * Action creators.
 */

export function setOnlineTeammatesAction(
  onlineTeammates: ReadonlyArray<OnlineTeammate>,
): SetOnlineTeammatesAction {
  return {
    type: OnlineTeammatesActionsEnum.SET_ONLINE_TEAMMATES,
    payload: onlineTeammates,
  };
}

export function updateIsTeammateOnlineAction(teammate: OnlineTeammate, isOnline: boolean): ThunkAction {
  return (dispatch, getState) => {
    const state = getState();
    const onlineTeammates = getOnlineTeammates(state);
    const uniqueOnlineTeammateIdentifiersSet = getUniqueOnlineTeammateIdentifiersSet(state);

    // If the initial load didn't happen, nothing to do.
    if (!onlineTeammates || !uniqueOnlineTeammateIdentifiersSet) {
      return null;
    }

    // If the status didn't change, nothing to do.
    const wasOnline = uniqueOnlineTeammateIdentifiersSet.has(buildUniqueOnlineTeammateIdentifier(teammate));
    if (isOnline === wasOnline) {
      return null;
    }

    // If so, create the new array.
    const newOnlineTeammates = isOnline
      ? concat(onlineTeammates, teammate)
      : onlineTeammates.filter((onlineTeammate) => !isEqual(onlineTeammate, teammate));

    // Dispatch the update action.
    const action: SetOnlineTeammatesAction = {
      type: OnlineTeammatesActionsEnum.SET_ONLINE_TEAMMATES,
      payload: newOnlineTeammates,
    };
    return dispatch(action);
  };
}

/*
 * Union type.
 */

export type OnlineTeammatesAction = SetOnlineTeammatesAction;
